import { useState, useEffect, useContext } from 'react';
import { AuthContext, apiClient } from '../App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Users, X, UserPlus, UserCheck } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Post-Game Discovery Component
 * 
 * Shows after a game is completed to encourage following players.
 * "Play with these players again?"
 * 
 * @param {Object} game - The completed game object
 * @param {function} onDismiss - Called when user dismisses the card
 */
const PostGameFollowPrompt = ({ game, onDismiss }) => {
  const { user, token } = useContext(AuthContext);
  const [players, setPlayers] = useState([]);
  const [followingMap, setFollowingMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user || !game) return;
    loadPlayers();
  }, [game?.id, user?.id]);

  const loadPlayers = async () => {
    try {
      setLoading(true);
      
      // Combine all players from both teams
      const allPlayers = [
        ...(game.team_a_players || []),
        ...(game.team_b_players || [])
      ].filter(p => p.user_id !== user?.id); // Exclude current user
      
      // Remove duplicates by user_id
      const uniquePlayers = allPlayers.reduce((acc, player) => {
        if (!acc.find(p => p.user_id === player.user_id)) {
          acc.push(player);
        }
        return acc;
      }, []);
      
      // Get following status for all players
      const followingRes = await apiClient.get('/follow/following');
      const followingIds = new Set(
        (followingRes.data.following || []).map(f => f.id)
      );
      
      // Build following map
      const map = {};
      uniquePlayers.forEach(p => {
        map[p.user_id] = followingIds.has(p.user_id);
      });
      
      setPlayers(uniquePlayers);
      setFollowingMap(map);
    } catch (error) {
      console.error('Failed to load players for follow prompt:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFollow = async (userId, username) => {
    try {
      if (followingMap[userId]) {
        await apiClient.delete(`/follow/${userId}`);
        setFollowingMap(prev => ({ ...prev, [userId]: false }));
        toast.success(`Unfollowed ${username}`);
      } else {
        await apiClient.post(`/follow/${userId}`);
        setFollowingMap(prev => ({ ...prev, [userId]: true }));
        toast.success(`Following ${username}`);
      }
    } catch (error) {
      toast.error('Action failed');
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    if (onDismiss) onDismiss();
  };

  // Don't show if dismissed, no players, or user is not logged in
  if (dismissed || !user || loading) return null;
  if (players.length === 0) return null;
  
  // Check if user was in this game
  const userWasInGame = [
    ...(game.team_a_players || []),
    ...(game.team_b_players || [])
  ].some(p => p.user_id === user.id);
  
  if (!userWasInGame) return null;

  return (
    <Card className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 border-orange-200" data-testid="post-game-follow-prompt">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-orange-600" />
          <h3 className="font-semibold text-zinc-900">Play with these players again?</h3>
        </div>
        <button 
          onClick={handleDismiss}
          className="text-zinc-400 hover:text-zinc-600 p-1"
          data-testid="dismiss-follow-prompt"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      
      <div className="space-y-2">
        {players.slice(0, 6).map((player) => (
          <div 
            key={player.user_id}
            className="flex items-center justify-between py-2 px-3 bg-white rounded-lg border border-zinc-100"
            data-testid={`follow-prompt-player-${player.user_id}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-zinc-200 flex items-center justify-center text-zinc-600 text-sm font-medium">
                {player.username?.charAt(0).toUpperCase() || 'P'}
              </div>
              <span className="text-sm font-medium text-zinc-900">{player.display_name || player.username}</span>
            </div>
            
            <Button
              onClick={() => handleFollow(player.user_id, player.username)}
              variant={followingMap[player.user_id] ? "outline" : "default"}
              size="sm"
              className={`h-8 px-3 rounded-full font-medium ${
                followingMap[player.user_id] 
                  ? 'text-zinc-600 border-zinc-300' 
                  : 'bg-orange-500 text-white hover:bg-orange-600'
              }`}
              data-testid={`follow-prompt-btn-${player.user_id}`}
            >
              {followingMap[player.user_id] ? (
                <>
                  <UserCheck className="w-3.5 h-3.5 mr-1" />
                  Following
                </>
              ) : (
                <>
                  <UserPlus className="w-3.5 h-3.5 mr-1" />
                  Follow
                </>
              )}
            </Button>
          </div>
        ))}
        
        {players.length > 6 && (
          <p className="text-xs text-zinc-500 text-center pt-2">
            +{players.length - 6} more players
          </p>
        )}
      </div>
    </Card>
  );
};

export default PostGameFollowPrompt;
